import { getInitialsFromString, joinNameParts, normalizeWhitespace } from '@/util/string-utils'

/**
 * Canonical profile snapshot the provider owns and FE-772's editor will replace on save.
 * `null` name fields mean "loaded, intentionally no name"; the snapshot being absent entirely
 * (provider holds `null`) means "canonical profile not loaded yet".
 */
export type UserProfileSnapshot = {
  firstName: string | null
  lastName: string | null
  email: string | null
}

/**
 * Inputs to the pure identity resolver.
 *
 * `firstName`/`lastName` use the undefined-vs-null convention:
 * - `undefined` → canonical profile not loaded; the transitional Firebase `displayName` may be used.
 * - `null` → canonical profile loaded with no name; Firebase is NOT consulted (a deliberately blank
 *   canonical name must not silently revert to stale Firebase data).
 */
export type ResolveUserIdentityInput = {
  firstName?: string | null
  lastName?: string | null
  email: string | null
  /** Transitional fallback only; consulted when the canonical name has not been loaded. */
  firebaseDisplayName?: string | null
}

export type UserIdentity = {
  /** Normalized preferred name, or null when the user has no usable name. */
  preferredName: string | null
  /** What navigation surfaces render: preferred name → email → "Account". */
  navigationLabel: string
  /** Normalized email, or null. Surfaced so consumers render the email header without touching `user`. */
  email: string | null
  /** Initials derived from a valid preferred name, or null (so callers can pick an icon fallback). */
  initials: string | null
  hasPreferredName: boolean
}

/**
 * Pure resolver that centralizes how the preferred name, navigation label, and initials are derived
 * from a (possibly partial) canonical profile with a transitional Firebase fallback. Keeping this
 * pure makes the full case matrix cheap to unit test and keeps consumers from recombining fields.
 */
export function resolveUserIdentity(input: ResolveUserIdentityInput): UserIdentity {
  const canonicalNotLoaded = input.firstName === undefined && input.lastName === undefined
  const canonicalName = joinNameParts(input.firstName, input.lastName)
  const firebaseFallback = canonicalNotLoaded ? normalizeWhitespace(input.firebaseDisplayName) : null
  const preferredName = canonicalName ?? firebaseFallback
  const email = normalizeWhitespace(input.email)

  return {
    preferredName,
    navigationLabel: preferredName ?? email ?? 'Account',
    email,
    initials: preferredName ? getInitialsFromString(preferredName) : null,
    hasPreferredName: preferredName !== null,
  }
}

/**
 * Inert identity (no name, no email → "Account"). Used as the context default and as a safety-net
 * fallback for consumers, so an inline AuthContext mock that omits `userIdentity` still type-checks
 * and renders sensibly without recombining raw user fields.
 */
export const EMPTY_USER_IDENTITY: UserIdentity = resolveUserIdentity({ email: null })
